'use client';

import { useEffect } from 'react';
import { useMatches } from 'react-router-dom';

/** Titolo usato quando nessuna rotta abbinata dichiara `handle.title`. Allineato a `index.html`. */
const FALLBACK_TITLE = 'SLIMS CER - Piattaforma Comunità Energetica';

/** Brand dopo il separatore. */
const SUFFIX = 'SLIMS CER';

/**
 * Punto mediano fra sezione e brand (scelta del product owner). Resta distinto
 * dal tratto lungo "–"/"—", che INB-103 vieta nel titolo del tab: quello non va
 * reintrodotto qui.
 */
const SEPARATOR = ' · ';

/** Metadati titolo dichiarati da una rotta tramite `handle` in `App.tsx`. */
export type RouteTitleHandle = {
	/** Nome della sezione mostrato nel tab del browser. */
	title?: string;
};

/**
 * Compone il titolo dalle rotte abbinate, ordinate da radice a foglia: l'ultima
 * che dichiara un valore vince, così una figlia sovrascrive il proprio padre.
 * Puro e senza dipendenze da React, per poterlo verificare in isolamento.
 */
export function resolveDocumentTitle(matches: readonly { handle?: unknown }[]): string {
	let title: string | undefined;

	for (const match of matches) {
		const handle = match.handle as RouteTitleHandle | undefined;
		if (handle?.title) title = handle.title;
	}

	return title ? `${title}${SEPARATOR}${SUFFIX}` : FALLBACK_TITLE;
}

/**
 * Tiene `document.title` allineato alla rotta corrente (INB-103). Montato una
 * sola volta nel layout radice: legge i `handle` delle rotte abbinate invece di
 * far impostare il titolo a ogni pagina.
 */
export function useDocumentTitle() {
	const matches = useMatches();

	useEffect(() => {
		document.title = resolveDocumentTitle(matches);
	}, [matches]);
}
